'use strict'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  makeStyles,
  Box,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Switch,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip
} from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5)
  },
  statsRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5)
  },
  tableContainer: {
    maxHeight: 'calc(100vh - 340px)',
    overflow: 'auto'
  },
  headerCell: {
    fontWeight: 700,
    fontSize: '0.82rem',
    whiteSpace: 'nowrap',
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 1
  },
  bodyCell: {
    fontSize: '0.82rem',
    padding: theme.spacing(0.75, 1.5)
  },
  routeName: {
    fontWeight: 600
  },
  excludedRow: {
    opacity: 0.45,
    backgroundColor: '#fafafa'
  },
  searchField: {
    minWidth: 200
  }
}))

function descendingComparator (a, b, orderBy) {
  const av = a[orderBy]
  const bv = b[orderBy]
  if (av == null && bv == null) return 0
  if (av == null) return 1
  if (bv == null) return -1
  if (typeof av === 'string') return bv.localeCompare(av)
  return bv < av ? -1 : bv > av ? 1 : 0
}

function getComparator (order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

export default function RoutesTab ({ url, onSnackbar, onRoutesChanged }) {
  const classes = useStyles()
  const [routesList, setRoutesList] = useState([])
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [showExcluded, setShowExcluded] = useState(true)
  const [order, setOrder] = useState('asc')
  const [orderBy, setOrderBy] = useState('routeName')
  const [pendingToggle, setPendingToggle] = useState(null)

  const fetchRoutes = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await axios.get(`${url}/api/hmi/territory/routes`)
      setRoutesList(resp.data?.routes || [])
      setMeta(resp.data?.meta || null)
    } catch (err) {
      console.error('Error loading routes:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to load routes', 'error')
    } finally {
      setLoading(false)
    }
  }, [url, onSnackbar])

  useEffect(() => {
    fetchRoutes()
  }, [fetchRoutes])

  const handleToggleExclude = async (route) => {
    const newExclude = !route.excludeFromTerritory
    setPendingToggle(route._id)
    try {
      await axios.patch(`${url}/api/hmi/territory/routes/${route._id}/exclude`, {
        exclude: newExclude
      })
      // Update local state
      setRoutesList(prev => prev.map(r =>
        r._id === route._id ? { ...r, excludeFromTerritory: newExclude } : r
      ))
      setMeta(prev => {
        if (!prev) return prev
        return {
          ...prev,
          excluded: prev.excluded + (newExclude ? 1 : -1),
          included: prev.included + (newExclude ? -1 : 1)
        }
      })
      if (onSnackbar) {
        onSnackbar(
          newExclude
            ? `${route.routeName || 'Route'} excluded from territory`
            : `${route.routeName || 'Route'} included in territory`,
          'success'
        )
      }
      if (onRoutesChanged) onRoutesChanged()
    } catch (err) {
      console.error('Error toggling exclude:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to update route', 'error')
    } finally {
      setPendingToggle(null)
    }
  }

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(column)
  }

  const filteredRoutes = useMemo(() => {
    let filtered = routesList
    if (!showExcluded) {
      filtered = filtered.filter(r => !r.excludeFromTerritory)
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      filtered = filtered.filter(r =>
        (r.routeName || '').toLowerCase().includes(q) ||
        (r.crewLeaderName || '').toLowerCase().includes(q) ||
        (r.crewName || '').toLowerCase().includes(q) ||
        (r.divisionName || '').toLowerCase().includes(q) ||
        String(r.routeId || '').includes(q)
      )
    }
    return filtered.slice().sort(getComparator(order, orderBy))
  }, [routesList, showExcluded, search, order, orderBy])

  const columns = [
    { id: 'routeName', label: 'Route Name' },
    { id: 'crewLeaderName', label: 'Crew Leader' },
    { id: 'crewName', label: 'Linked Crew' },
    { id: 'routeSize', label: 'Size' },
    { id: 'divisionName', label: 'Division' },
    { id: 'routeId', label: 'Aspire ID' },
    { id: 'excludeFromTerritory', label: 'Included' }
  ]

  return (
    <div>
      <div className={classes.toolbar}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search routes..."
          variant="outlined"
          size="small"
          className={classes.searchField}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={showExcluded ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
          onClick={() => setShowExcluded(prev => !prev)}
        >
          {showExcluded ? 'Showing All' : 'Hiding Excluded'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
          onClick={fetchRoutes}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>

      {meta && (
        <div className={classes.statsRow}>
          <Chip size="small" variant="outlined" label={`${meta.total} total`} />
          <Chip size="small" variant="outlined" label={`${meta.included} included`} style={{ borderColor: '#4CAF50', color: '#4CAF50' }} />
          {meta.excluded > 0 && (
            <Chip size="small" variant="outlined" label={`${meta.excluded} excluded`} style={{ borderColor: '#f44336', color: '#f44336' }} />
          )}
          <Chip size="small" variant="outlined" label={`${meta.withCrew} with crew`} />
          {meta.withoutCrew > 0 && (
            <Chip size="small" variant="outlined" label={`${meta.withoutCrew} no crew`} style={{ borderColor: '#ff9800', color: '#ff9800' }} />
          )}
        </div>
      )}

      {loading && !routesList.length ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map(col => (
                  <TableCell key={col.id} className={classes.headerCell}>
                    {col.id === 'excludeFromTerritory' ? (
                      col.label
                    ) : (
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : 'asc'}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRoutes.map(route => (
                <TableRow
                  key={route._id}
                  className={route.excludeFromTerritory ? classes.excludedRow : undefined}
                  hover
                >
                  <TableCell className={classes.bodyCell}>
                    <span className={classes.routeName}>{route.routeName || '-'}</span>
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {route.crewLeaderName || '-'}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {route.crewName ? (
                      <Tooltip title={`Crew ID: ${route.crewId || '?'}`} arrow>
                        <span>{route.crewName}</span>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" style={{ color: '#ff9800', fontSize: '0.82rem' }}>No crew linked</Typography>
                    )}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {route.routeSize || '-'}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {route.divisionName || '-'}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {route.routeId || '-'}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    <Switch
                      checked={!route.excludeFromTerritory}
                      onChange={() => handleToggleExclude(route)}
                      disabled={pendingToggle === route._id}
                      size="small"
                      color="primary"
                    />
                    {pendingToggle === route._id && (
                      <CircularProgress size={14} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filteredRoutes.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Typography style={{ color: '#999', padding: 16 }}>
                      {search ? 'No routes match your search.' : 'No routes found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  )
}
